
import React, { useMemo } from "react";

const scrollItems = [
  "Home Interiors",
  "Modular Kitchens",
  "Wardrobes",
  "Living Room Design",
  "False Ceiling",
  "House Construction",
  "Office Interiors",
  "Custom Furniture",
  "Lighting Design",
  "Interior Wall Painting",
  "Exterior Painting",
  "Texture Painting",
  "Waterproofing",
  "Door & Furniture Painting",
  "Commercial Painting",
];

const activeItems = new Set([
  "Home Interiors",
  "House Construction",
  "Interior Wall Painting",
  "Waterproofing",
]);

function InfiniteScroll() {
  const duplicatedItems = useMemo(
    () => [...scrollItems, ...scrollItems],
    []
  );

  return (
    <section className="relative w-full overflow-hidden bg-slate-50 py-16 font-['DM_Sans',sans-serif] sm:py-20 lg:py-28 xl:py-32">
      <div className="mx-auto grid w-full max-w-[1440px] grid-cols-1 items-center gap-12 px-5 sm:px-8 md:px-10 lg:grid-cols-2 lg:gap-16 lg:px-12 xl:gap-24 xl:px-20">
        {/* LEFT CONTENT */}
        <div className="flex min-w-0 flex-col items-center text-center lg:items-start lg:text-left">
          {/* Small heading */}
          <span className="mb-6 inline-flex max-w-full items-center gap-2 rounded-full border border-indigo-100 bg-indigo-50 px-4 py-2 font-['Poppins',sans-serif] text-[9px] font-semibold uppercase tracking-[0.12em] text-indigo-700 sm:gap-3 sm:px-5 sm:text-[10px] sm:tracking-[0.17em]">
            <span className="size-1.5 shrink-0 rounded-full bg-indigo-600" />
            <span>INTERIOR DESIGN · CONSTRUCTION · PAINTING</span>
          </span>

          {/* Main heading */}
          <h2 className="max-w-[720px] font-['Poppins',sans-serif] text-[clamp(2.25rem,5vw,4.75rem)] font-semibold leading-[1.13] tracking-[-0.055em] text-slate-950">
            We Design It.
            <br />
            We Build It.
            <br />
            <span className="text-indigo-600">We Paint It.</span>
          </h2>

          {/* Accent line */}
          <div className="mt-7 flex items-center gap-2 lg:mt-8">
            <span className="h-1 w-12 rounded-full bg-indigo-600" />
            <span className="h-1 w-4 rounded-full bg-indigo-200" />
          </div>

          {/* Description */}
          <p className="mt-6 max-w-[580px] text-sm leading-7 text-slate-600 sm:text-base sm:leading-8 lg:text-lg">
            Cube4Spaces and Paint Studio work together to make your spaces
            look beautiful and feel comfortable. From home interiors and
            construction to wall painting and waterproofing, we help bring
            your ideas to life.
          </p>

          {/* Brand cards */}
          <div className="mt-8 grid w-full max-w-[580px] grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:mt-9">
            <div className="rounded-xl border border-slate-200 bg-white p-5 text-left shadow-sm transition duration-300 hover:-translate-y-1 hover:border-indigo-200 hover:shadow-lg sm:p-5">
              <div className="mb-4 flex size-10 items-center justify-center rounded-lg bg-indigo-50 text-indigo-700">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="size-5"
                  aria-hidden="true"
                >
                  <path d="M3 10.5 12 3l9 7.5" />
                  <path d="M5.5 9.5V21h13V9.5" />
                  <path d="M9 21v-7h6v7" />
                </svg>
              </div>

              <h3 className="font-['Poppins',sans-serif] text-base font-semibold tracking-tight text-slate-950">
                Cube4Spaces
              </h3>

              <p className="mt-2 text-sm leading-6 text-slate-500">
                Interior Design &amp; Construction
              </p>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-5 text-left shadow-sm transition duration-300 hover:-translate-y-1 hover:border-indigo-200 hover:shadow-lg sm:p-5">
              <div className="mb-4 flex size-10 items-center justify-center rounded-lg bg-indigo-50 text-indigo-700">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="size-5"
                  aria-hidden="true"
                >
                  <path d="m14 6 4-4 4 4-4 4" />
                  <path d="m18 6-9 9" />
                  <path d="m5 15-3 7 7-3" />
                  <path d="m12 8 4 4" />
                </svg>
              </div>

              <h3 className="font-['Poppins',sans-serif] text-base font-semibold tracking-tight text-slate-950">
                Paint Studio
              </h3>

              <p className="mt-2 text-sm leading-6 text-slate-500">
                Home Painting &amp; Commercial Painting
              </p>
            </div>
          </div>

          {/* Closing brand statement */}
          <div className="mt-8 border-l-2 border-indigo-600 pl-4 text-left sm:mt-9">
            <p className="font-['Poppins',sans-serif] text-sm font-medium leading-6 text-slate-800 sm:text-base">
              Everything Your Space Needs,
              <span className="text-indigo-600">
                {" "}All in One Place.
              </span>
            </p>

            <p className="mt-2 text-xs leading-5 text-slate-500 sm:text-sm">
              Cube4Spaces × Paint Studio — Design, Build, Paint &amp; Protect.
            </p>
          </div>
        </div>

        {/* RIGHT INFINITE VERTICAL SCROLL */}
        <div className="relative mx-auto w-full min-w-0 max-w-[650px] lg:max-w-none">
          {/* Section label */}
          <div className="mb-5 flex items-center justify-between gap-4">
            <span className="font-['Poppins',sans-serif] text-[10px] font-semibold uppercase tracking-[0.17em] text-slate-500 sm:text-xs">
              Explore Our Services
            </span>

            <span className="text-xs font-medium text-indigo-600">
              15 Services
            </span>
          </div>

          <div className="relative h-[350px] w-full overflow-hidden rounded-2xl border border-slate-200 bg-white px-5 shadow-[0_20px_60px_rgba(15,23,42,0.06)] sm:h-[410px] sm:px-8 lg:h-[450px] lg:px-9">
            {/* Top and bottom fade */}
            <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-20 bg-gradient-to-b from-white via-white/90 to-transparent sm:h-24" />

            <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-20 bg-gradient-to-t from-white via-white/90 to-transparent sm:h-24" />

            {/* Scrolling track */}
            <div className="infinite-services-track flex flex-col">
              {duplicatedItems.map((item, index) => {
                const isActive = activeItems.has(item);

                return (
                  <div
                    key={`${item}-${index}`}
                    className="group flex min-h-[57px] items-center gap-3 border-b border-slate-100 py-3 sm:min-h-[66px] sm:gap-5"
                  >
                    <span
                      className={`flex size-7 shrink-0 items-center justify-center rounded-md font-['Poppins',sans-serif] text-[9px] font-semibold transition-colors duration-300 sm:size-8 sm:text-[10px] ${
                        isActive
                          ? "bg-indigo-600 text-white"
                          : "bg-slate-100 text-slate-400 group-hover:bg-indigo-50 group-hover:text-indigo-600"
                      }`}
                    >
                      {String((index % scrollItems.length) + 1).padStart(
                        2,
                        "0"
                      )}
                    </span>

                    <span
                      className={`min-w-0 font-['Poppins',sans-serif] text-[clamp(0.95rem,2.5vw,1.55rem)] font-semibold leading-snug tracking-tight transition-colors duration-300 sm:text-2xl lg:text-[25px] ${
                        isActive
                          ? "text-slate-950"
                          : "text-slate-300 group-hover:text-indigo-600"
                      }`}
                    >
                      {item}
                    </span>

                    <span
                      className={`ml-auto shrink-0 transition-all duration-300 ${
                        isActive
                          ? "translate-x-0 opacity-100"
                          : "-translate-x-1 opacity-0 group-hover:translate-x-0 group-hover:opacity-100"
                      }`}
                      aria-hidden="true"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="size-4 text-indigo-600 sm:size-5"
                      >
                        <path d="M5 12h14" />
                        <path d="m12 5 7 7-7 7" />
                      </svg>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Bottom information */}
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs leading-5 text-slate-500 sm:text-sm">
              From the first sketch to the final finish.
            </p>

            <div className="flex items-center gap-2">
              <span className="size-2 rounded-full bg-emerald-500" />
              <span className="text-[10px] font-medium uppercase tracking-wider text-slate-500 sm:text-xs">
                Complete Space Solutions
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Animation styles */}
      <style>{`
        .infinite-services-track {
          animation: cubeVerticalScroll 42s linear infinite;
          will-change: transform;
          transform: translate3d(0, 0, 0);
          backface-visibility: hidden;
        }

        @keyframes cubeVerticalScroll {
          from {
            transform: translate3d(0, 0, 0);
          }

          to {
            transform: translate3d(0, -50%, 0);
          }
        }

        @media (hover: hover) {
          .infinite-services-track:hover {
            animation-play-state: paused;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .infinite-services-track {
            animation: none;
            will-change: auto;
          }
        }
      `}</style>
    </section>
  );
}

export default React.memo(InfiniteScroll);
